
import React from "react";
import ReactDOM from "react-dom/client";
import { NavLink } from "react-router-dom";
import { i18nManager } from "../../../../../i18n";
import { Status, Subscriber, Utils } from "../../../../../utils";
import { ConfirmDialog, Elements, SidedContainer, SidedContainerProps, Siren } from "../../../../shared";
import { BoolHelper, ButtonManageRef, Classname, Column, NoseurElement, NoseurObject, ObjectHelper, StringHelper, Table, useSearchParams } from "@rivtn/noseur";
import { AdministrationService, ApiResponseData, BaseService, IAdministrator, ICrmLead, ICrmLeadNote, IPlatform, PipelineService, SanitizedResponse } from "../../../../../services";

export interface LeadsProps {
    platform: IPlatform;
}

function Leads(props: LeadsProps) {

    const pipelineService = PipelineService.getInstance();
    const administrationService = AdministrationService.getInstance();
    const activeDialogMutableRoot = React.useRef<ReactDOM.Root>(null);
    const [crmLeadsStat, setCrmLeadsStat] = React.useState<NoseurObject<any>>();
    const [crmLeads, setCrmLeads] = React.useState<ApiResponseData<ICrmLead>>({});
    const labels = i18nManager.Labels.dashboard.platform_management.crm.pipeline.leads;
    const [activeCrmLead, setActiveCrmLead] = React.useState<{ crmLead?: ICrmLead, activeData?: NoseurObject<any>, mainContent?: () => NoseurElement }>({});
    const { searchParams, setSearchParams, status, setStatus } = useSearchParams({
        defaultInit: window.location.search,
        params: {
            status: "",
        }
    });

    React.useEffect(() => {
        if (window.location.search.includes("active_data_id")) {
            if (!activeCrmLead.crmLead) {
                fetchCrmLead(searchParams.get("active_data_id"));
            }
            return;
        }
        searchParams.delete("active_data_id");
        setActiveCrmLead({});
        if (crmLeads.content) {
            fetchCrmLeads();
            fetchCrmLeadsStat();
        }
    }, [searchParams]);

    return SidedContainer({
        searchParams,
        setSearchParams,
        onAction: manageCrmLead,
        labels: labels.sided_labels,
        secondStyle: { overflow: "auto" },
        activeData: activeCrmLead.activeData,
        mainContent: activeCrmLead.mainContent ?? buildListingTable,
        thirdStyle: { overflow: "auto", borderLeft: "15px solid #F0F0F0", marginTop: -41, marginBottom: -15, borderRadius: 0 },
        queryData: () => {
            fetchCrmLeads();
            fetchCrmLeadsStat();
        },
    } as any);

    function fetchCrmLeadsStat() {
        setCrmLeadsStat(undefined);
        pipelineService.queryCrmLeadsStat({
            ...Utils.normalizeUrlParams(searchParams),
            "platform.platform_id": searchParams.get("platform_id") ?? "",
        }).then(({ sanitized }: SanitizedResponse<any>) => {
            const pipelineCount = parseFloat("" + (sanitized.crm_leads_count ?? "0"));
            const pipelineRevenue = parseFloat("" + (sanitized.potential_value_sum ?? "0"));
            const wonCount = parseInt("" + (sanitized.top_status_count?.[Status.CLOSED_WON] ?? "0"));
            const inProgressCount = parseInt("" + (sanitized.top_status_count?.[Status.NEW] ?? "0")) + parseInt("" + (sanitized.top_status_count?.[Status.MEETING] ?? "0"))
                + parseInt("" + (sanitized.top_status_count?.[Status.CONTACTED] ?? "0")) + parseInt("" + (sanitized.top_status_count?.[Status.NEGOTIATION] ?? "0"));
            const lostCount = parseInt("" + (sanitized.top_status_count?.[Status.CLOSED_LOST] ?? "0")) + parseInt("" + (sanitized.top_status_count?.[Status.CLOSED_INDECISIVE] ?? "0"));
            const inProgressRevenue = StringHelper.toPresentableMoneyValue(((pipelineRevenue * (parseFloat("" + (sanitized.top_status_percentage?.[Status.NEW] ?? "0"))
                + parseFloat("" + (sanitized.top_status_percentage?.[Status.MEETING] ?? "0")) + parseFloat("" + (sanitized.top_status_percentage?.[Status.CONTACTED] ?? "0")) + parseFloat("" + (sanitized.top_status_percentage?.[Status.NEGOTIATION] ?? "0")))) / 100));
            const lostRevenue = StringHelper.toPresentableMoneyValue(((pipelineRevenue * (parseFloat("" + (sanitized.top_status_percentage?.[Status.CLOSED_LOST] ?? "0")) + parseFloat("" + (sanitized.top_status_percentage?.[Status.CLOSED_INDECISIVE] ?? "0")))) / 100));
            setCrmLeadsStat({
                ...sanitized,
                won_count: "" + wonCount,
                leads_won: "" + wonCount,
                lost_count: "" + lostCount,
                pipeline_count: pipelineCount,
                leads_total: "" + pipelineCount,
                lost_revenue: "$ " + lostRevenue,
                in_progress_count: "" + inProgressCount,
                in_progress_revenue: "$ " + inProgressRevenue,
                conversion: ((wonCount / pipelineCount) * 100) + "%",
                pipeline_value: "$ " + StringHelper.toPresentableMoneyValue(pipelineRevenue),
                total_revenue: "$ " + StringHelper.toPresentableMoneyValue(((pipelineRevenue * parseFloat("" + (sanitized.top_status_percentage?.[Status.CLOSED_WON] ?? "0"))) / 100)),
            });
        }).catch(BaseService.reportError);
    }

    function fetchCrmLeads() {
        setCrmLeads({});
        pipelineService.queryCrmLeads({
            ...Utils.normalizeUrlParams(searchParams),
            "platform.platform_id": searchParams.get("platform_id") ?? "",
        }).then(({ apiResponse }: SanitizedResponse<ICrmLead>) => {
            setCrmLeads(apiResponse.data!);
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function fetchCrmLead(externalId: string | null) {
        if (!externalId) return;
        pipelineService.getSingleCrmLead(externalId).then(({ apiResponse }: SanitizedResponse<ICrmLead>) => {
            setActiveCrmLeadProps(apiResponse.data as any as ICrmLead);
        }).catch((err) => {
            BaseService.reportError(err);
            Utils.updateSearchParams("active_data_id", undefined, setSearchParams, fetchCrmLeads);
        }).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function setActiveCrmLeadProps(crmLead: ICrmLead) {
        setActiveCrmLead({
            crmLead: crmLead,
            activeData: {
                element: buildActiveData(crmLead)
            },
        });
    }

    function buildListingTable() {
        return (<React.Fragment>
            <div className="trans-cards" style={{ height: "fit-content", paddingBottom: 0, marginBottom: 15 }}>
                {labels.stats.filter((stat) => !(BoolHelper.equalsAny(stat.id, ["conversion", "lost_revenue"]) && activeCrmLead.crmLead)).map((stat: { id: string; color: string; desc: string; label: string; }) => (<div key={stat.id} className="trans-card" style={{
                    flex: 1,
                    borderColor: stat.color,
                    boxShadow: `0px 4px 4px ${stat.color}40`,
                }}>
                    <div className="header">
                        {Elements.buildTransCardLogo(stat.color)}
                        <span>{stat.label}</span>
                    </div>

                    {crmLeadsStat ? (<div className="content">
                        <span className="amount">{crmLeadsStat?.[stat.id]}</span>
                        <span className="desc">{ObjectHelper.expandStringTemplate(stat.desc, crmLeadsStat, { chop: "$" })}</span>
                    </div>) : Elements.LOADING_STATE}
                </div>))}
            </div>
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", marginBottom: 15, alignItems: "center" }}>
                <div className="leads-tags">
                    <i className="fa fa-bar-chart" />
                    {labels.statuses.map((s) => (<div key={s.value} className={Classname.build("noseur-pill", { active: s.value === status })} onClick={() => setStatus(s.value)}>{s.label}</div>))}
                </div>
                {(!activeCrmLead.crmLead) && (<span className="link" onClick={() => manageCrmLead()}>{labels.add_leads}</span>)}
            </div>
            <Table rowProps={{ className: "listing-table-row" }} rowsPerPage={crmLeads.size ?? 10} className="table" style={{ marginTop: 0 }} data={crmLeads.content} totalRecords={crmLeads.total_elements} dataRefreshKeys={["external_id"]} loadingState={Elements.LOADING_STATE} emptyState={<div className="es" style={{ marginTop: 20 }}>
                <i className="fa fa-bullseye" />
                <span style={{ maxWidth: 150 }}>{i18nManager.Labels.common.no} {labels.sided_labels.fine_name} {i18nManager.Labels.common.found} <span onClick={() => manageCrmLead()}>{labels.sided_labels.create ?? i18nManager.Labels.common.create}</span> {labels.sided_labels.fine_name}</span>
            </div>} valuedRowProps={(crmLead: any) => {
                if (crmLead.external_id === activeCrmLead.crmLead?.external_id) {
                    return { className: "listing-table-row active" };
                }
                return {};
            }} onRowSelection={(crmLead: ICrmLead) => {
                setActiveCrmLeadProps(crmLead);
                Utils.updateSearchParams("active_data_id", crmLead.external_id, setSearchParams);
                return false;
            }}>
                <Column header={labels.table.company} dataKey="company_name" />
                <Column header={labels.table.contact} template={(crmLead: ICrmLead) => {
                    return (<div style={{ display: "flex", flexDirection: "column" }}>
                        <span>{crmLead.contact_name}</span>
                        <span style={{ fontSize: "90%" }}>{crmLead.contact_email}</span>
                    </div>);
                }} />
                <Column header={labels.table.stage} template={(crmLead: ICrmLead) => selectTableStatusStyling(crmLead)} />
                <Column header={labels.table.value} template={(crmLead: ICrmLead) => (`${crmLead.extra_data?.currency ?? "$"} ${StringHelper.toPresentableMoneyValue(crmLead.potential_value ?? 0)}`)} />
                <Column doNotRender={!!activeCrmLead.crmLead} header={labels.table.last_touch} template={(crmLead: ICrmLead) => Utils.formatToFineDate(crmLead.updated_at)} />
                <Column doNotRender={!!activeCrmLead.crmLead} header={labels.table.manager} template={(crmLead: ICrmLead) => {
                    return (<NavLink className="lined-limit" style={{ width: "fit-content" }} to={{
                        search: `active_admin_id=${crmLead.administrator.external_id}`,
                        pathname: "/dashboard/administration/management"
                    }}>{crmLead.administrator.first_name} {crmLead.administrator.last_name}</NavLink>);
                }} />
                <Column template={(_: ICrmLead) => <i style={{ cursor: "pointer" }} className="fa fa-angle-right" />} />
            </Table>
            {Elements.buildPaginator(crmLeads ?? {}, ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, id: null, size }, setSearchParams, fetchCrmLeads), parseInt(searchParams.get("page") ?? "1"))}
        </React.Fragment>);
    }

    function selectTableStatusColor(data?: { status: Status }) {
        if (data?.status === Status.MEETING) return "#B3C322";
        if (data?.status === Status.CONTACTED) return "#D4319E";
        if (data?.status === Status.CLOSED_WON) return "#0FA883";
        if (data?.status === Status.NEGOTIATION) return "#3C31D4";
        if (data?.status === Status.CLOSED_LOST) return "#EB5959";
        if (data?.status === Status.CLOSED_INDECISIVE) return "#000000";
        return "#618CF1";
    }

    function selectTableStatusStyling(data?: { status: Status }) {
        return (<div className="noseur-pill" style={{ background: selectTableStatusColor(data), color: "white", padding: "0.25rem 0.8rem" }}>{StringHelper.toSaneSentenceFormat(data?.status ?? "NEW")}</div>);
    }

    function buildActiveData(crmLead: ICrmLead) {

        (async () => {
            pipelineService.queryCrmLeadsNotes(crmLead.external_id).then(({ apiResponse }: SanitizedResponse<ICrmLeadNote>) => {
                if (!activeDialogMutableRoot?.current) return;
                activeDialogMutableRoot.current.render(apiResponse.data!.content?.map((note) => (<div key={note.external_id}
                    style={{ borderRadius: 7, backgroundColor: "rgba(15, 168, 131, 0.06)", padding: 10, marginBottom: 10, width: "auto", display: "flex", flexDirection: "column" }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{Utils.formatToFineDate(note.created_at)} - {note.administrator.first_name} {note.administrator.last_name}</div>
                    <div style={{ fontSize: 12, marginTop: 3 }}><span style={{ fontWeight: "bold", color: selectTableStatusColor(note) }}>{StringHelper.toSaneSentenceFormat(note?.status ?? "NEW")}</span> - {note.note}</div>
                </div>)));
            }).catch(BaseService.reportError);
        })();

        if (activeDialogMutableRoot?.current) activeDialogMutableRoot?.current.render(Elements.LOADING_STATE);
        return (<React.Fragment>
            <div className="header">
                <i className="fa fa-times" onClick={() => {
                    setActiveCrmLead({});
                    Utils.updateSearchParams("active_data_id", undefined, setSearchParams, fetchCrmLeads);
                }} />
                <span className="name">{crmLead.contact_name}</span>
                <span className="name" style={{ marginTop: 5, color: "#3C31D4", fontSize: 15 }}>{crmLead.company_name}</span>
            </div>
            <div className="middle">
                <div><b>{labels.active_lead.stage}:</b> {selectTableStatusStyling(crmLead)}</div>
                <div><b>{labels.active_lead.deal_value}:</b> $ {StringHelper.toPresentableMoneyValue(crmLead.potential_value)}</div>
                <div style={{ display: "flex" }}>
                    <b>{labels.active_lead.manager}:</b>
                    <NavLink className="lined-limit" style={{ width: "fit-content", marginLeft: 5 }} to={{
                        search: `active_admin_id=${crmLead.administrator.external_id}`,
                        pathname: "/dashboard/administration/management"
                    }}>{crmLead.administrator.first_name} {crmLead.administrator.last_name}</NavLink>
                </div>
                <div><b>{labels.active_lead.contact_email}:</b> {crmLead.contact_email}</div>
                <div><b>{labels.active_lead.contact_phone}:</b> {crmLead.contact_phone}</div>
                <div><b>{labels.active_lead.date_added}:</b> {Utils.formatToFineDate(crmLead.created_at)}</div>
                <div><b>{labels.active_lead.last_touch}:</b> {Utils.formatToFineDate(crmLead.updated_at)}</div>
                <div><b>{labels.active_lead.next_followup_date}:</b> {Utils.formatToFineDate(crmLead.next_follow_up_date)}</div>
            </div>
            <div className="actions" style={{ margin: "0px 5px", borderBottom: "1px solid #F0F0F0", paddingBottom: 15 }}>
                <span className="link" onClick={() => manageCrmLead(crmLead)}>{labels.active_lead.edit_lead}</span>
                <span className="link" onClick={() => updateLeadStage(crmLead)}>{labels.active_lead.update_stage}</span>
            </div>
            <div className="actions" style={{ margin: 5, }}>
                <span style={{ marginBottom: 5 }}>{labels.active_lead.activity_timeline}</span>
                <div style={{ display: "flex", flexDirection: "column", width: "auto" }} ref={(r) => {
                    if (!r) return;
                    ObjectHelper.resolveRef(activeDialogMutableRoot, ReactDOM.createRoot(r));
                }}>
                    {Elements.LOADING_STATE}
                </div>
            </div>
        </React.Fragment>);
    }

    function manageCrmLead(crmLead?: ICrmLead) {
        let dialogErrorRef: any;
        const isUpdate = !!crmLead;
        const dialogLabels = { ...labels.dialog.add_update };
        const payload: any = { platform_id: props.platform.platform_id, status: Status.NEW, ...crmLead, administrator_id: crmLead?.administrator?.external_id };

        if (isUpdate) {
            dialogLabels.form_post = dialogLabels.form_post.filter((f) => f.name != "note");
        }

        const dialog = ConfirmDialog({
            closable: true,
            style: { minWidth: 400 },
            title: (isUpdate ? dialogLabels.update_title : dialogLabels.add_title),
            confirmLabel: (isUpdate ? dialogLabels.update_lead : dialogLabels.create_lead),
            content: (<div>
                {Elements.buildConfirmDialogForm(dialogLabels.form_pre, (name: string, value: any) => {
                    dialogErrorRef.innerText = " ";
                    payload[name] = value;
                }, payload)}
                {Elements.buildConfirmDialogForm(dialogLabels.form_row, (name: string, value: any) => {
                    dialogErrorRef.innerText = " ";
                    if (name === "potential_value") {
                        payload[name] = parseInt("" + (parseFloat(value) * 1000000));
                    } else if (name === "next_follow_up_date") {
                        payload[name] = Utils.formatToApiDate(value.selectedDate);
                    }
                }, { ...payload, potential_value: ((crmLead?.potential_value ?? 0) / 1000000) }, undefined, "add-leads-row-form")}
                {Elements.buildConfirmDialogForm(dialogLabels.form_post, (name: string, value: any, searching) => {
                    dialogErrorRef.innerText = " ";
                    if (searching && name === "administrator_id") {
                        if (!value.value.length) return [];
                        administrationService.queryAdministrator({ email: value.value }).then(({ apiResponse }: SanitizedResponse<IAdministrator>) => {
                            value.manageRef?.changeOptions(apiResponse.data?.content?.map((a) => ({
                                value: a.external_id,
                                icon: a.profile_picture_location,
                                label: `${a.first_name} ${a.last_name}`,
                            })));
                        }).catch(BaseService.reportError);
                        return false;
                    }
                    payload[name] = value;
                }, payload, undefined, undefined, {
                    administrator_id: (crmLead?.administrator ? [{ value: crmLead.administrator.external_id, label: `${crmLead.administrator.first_name} ${crmLead.administrator.last_name}`, icon: crmLead.administrator.profile_picture_location }] : [])
                })}
                <div ref={(r) => { dialogErrorRef = r; }} className="error"> </div>
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                dialogErrorRef.innerText = " ";
                if (payload.next_follow_up_date?.includes("T")) payload["next_follow_up_date"] = Utils.formatToApiDate(new Date(payload.next_follow_up_date));
                const method = (isUpdate ? pipelineService.updateCrmLead : pipelineService.createCrmLead).bind(pipelineService);
                method(payload).then(() => {
                    Siren.alert(isUpdate ? dialogLabels.update_successful : dialogLabels.add_successful);
                    dialog.hide(() => {
                        fetchCrmLeadsStat();
                        if (isUpdate) {
                            fetchCrmLead(payload.external_id);
                            return;
                        }
                        fetchCrmLeads();
                    });
                }).catch((err) => {
                    BaseService.reportError(err);
                    dialogErrorRef.innerText = err?.message ?? err;
                }).finally(() => button?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

    function updateLeadStage(crmLead: ICrmLead) {
        let dialogErrorRef: any;
        const payload: any = {};
        const dialogLabels = labels.dialog.update_stage;

        const dialog = ConfirmDialog({
            closable: true,
            style: { minWidth: 400 },
            title: dialogLabels.title,
            confirmLabel: dialogLabels.update_lead,
            content: (<div>
                <div style={{ display: "flex", flexDirection: "column", margin: "10px 0px" }}>
                    <span style={{ fontWeight: "bold" }}>{crmLead.contact_name}</span>
                    <span style={{ marginTop: 5, color: "#3C31D4", fontSize: 15 }}>{crmLead.company_name}</span>
                </div>
                {Elements.buildConfirmDialogForm(dialogLabels.form, (name: string, value: any) => {
                    dialogErrorRef.innerText = " ";
                    payload[name] = value;
                }, payload)}
                <div ref={(r) => { dialogErrorRef = r; }} className="error"> </div>
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                dialogErrorRef.innerText = " ";
                pipelineService.updateCrmLeadStage(crmLead.external_id, payload).then(() => {
                    Siren.alert(dialogLabels.successful);
                    dialog.hide(() => {
                        fetchCrmLeadsStat();
                        fetchCrmLead(crmLead.external_id);
                    });
                }).catch((err) => {
                    BaseService.reportError(err);
                    dialogErrorRef.innerText = err?.message ?? err;
                }).finally(() => button?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

}

export default Leads;
